import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { api, clientFetch, type Issue, type LotPage, type LotView, type Settings, type SourceHealth, type Stance } from "./api.ts";
import { connection, deleting, held, toast } from "./store.ts";

export const keys = {
  lots: ["lots"] as const,
  archived: ["lots", "archived"] as const,
  lot: (id: string) => ["lot", id] as const,
  issues: ["issues"] as const,
  sources: ["sources"] as const,
  settings: ["settings"] as const,
  tags: ["tags"] as const,
  session: ["session"] as const,
};

export const useLots = () => useQuery({ queryKey: keys.lots, queryFn: () => api<LotView[]>("/lots") });
export const useArchived = () => useQuery({ queryKey: keys.archived, queryFn: () => api<LotView[]>("/lots?archived=1") });
export const useLot = (id: string) => useQuery({ queryKey: keys.lot(id), queryFn: () => api<LotPage>(`/lots/${id}`) });
export const useIssues = () => useQuery({ queryKey: keys.issues, queryFn: () => api<Issue[]>("/issues") });
export const useSources = () => useQuery({ queryKey: keys.sources, queryFn: () => api<SourceHealth[]>("/sources") });
export const useSettings = () => useQuery({ queryKey: keys.settings, queryFn: () => api<Settings>("/settings") });
export const useTags = () => useQuery({ queryKey: keys.tags, queryFn: () => api<{ name: string; count: number }[]>("/tags") });

/** Write a changed lot into every cached list so the UI updates before the refetch lands. */
export function patchLotInCache(qc: QueryClient, id: string, fn: (l: LotView) => LotView) {
  qc.setQueryData<LotView[]>(keys.lots, (old) => old?.map((l) => (l.id === id ? fn(l) : l)));
  qc.setQueryData<LotPage>(keys.lot(id), (old) => (old ? ({ ...old, ...fn(old) } as LotPage) : old));
}

export function useSetStance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; stance: Stance; maxBidCents?: number | null; alertUnderCents?: number | null; note?: string }) =>
      api<LotView>(`/lots/${v.id}/stance`, { method: "PUT", body: { stance: v.stance, maxBidCents: v.maxBidCents, alertUnderCents: v.alertUnderCents, note: v.note } }),
    onMutate: (v) => {
      const prev = qc.getQueryData<LotView[]>(keys.lots)?.find((l) => l.id === v.id)?.stance;
      patchLotInCache(qc, v.id, (l) => ({
        ...l,
        stance: {
          ...l.stance,
          stance: v.stance,
          ...(v.maxBidCents !== undefined && { maxBidCents: v.maxBidCents }),
          ...(v.alertUnderCents !== undefined && { alertUnderCents: v.alertUnderCents }),
          ...(v.note !== undefined && { note: v.note }),
        },
      }));
      return { prev };
    },
    onError: (e, v, ctx) => {
      if (ctx?.prev) patchLotInCache(qc, v.id, (l) => ({ ...l, stance: ctx.prev! }));
      toast(`Couldn't save: ${(e as Error).message}`, { tone: "error" });
    },
    onSettled: (_d, _e, v) => {
      void qc.invalidateQueries({ queryKey: keys.lot(v.id) });
    },
  });
}

/** Undoable stance change: what the row control and w/b/p keys call. */
export function useStanceWithUndo() {
  const m = useSetStance();
  return (lot: LotView, stance: Stance) => {
    if (lot.stance.stance === stance) return;
    const prev = lot.stance.stance;
    if (stance === "bidding") {
      held.set((h) => { const n = new Map(h); n.delete(lot.id); return n; });
    } else {
      held.set((h) => (h.has(lot.id) ? h : new Map(h).set(lot.id, prev === "bidding" ? "priority" : "time")));
    }
    m.mutate({ id: lot.id, stance });
    const label = { watching: "Watching", bidding: "Bidding", passed: "Passed" }[stance];
    toast(`${label}: ${lot.title}`, { action: { label: "Undo", run: () => m.mutate({ id: lot.id, stance: prev }) } });
  };
}

export function useInvalidateAll() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries();
}

/**
 * Follow the server's live events and refetch what changed. The Node server streams them over SSE;
 * on Cloudflare they come over a (hibernating) WebSocket. Reconnects with backoff either way.
 */
export function useLiveUpdates() {
  const qc = useQueryClient();
  const transport = useQuery({ queryKey: keys.session, queryFn: () => api<{ liveTransport?: "sse" | "ws" }>("/session"), staleTime: 60_000 }).data?.liveTransport;
  useEffect(() => {
    if (!transport) return;
    let closed = false;
    let retry = 1000;
    let timer: number | undefined;
    let ping: number | undefined;
    let pending = new Set<string>();
    let flush: number | undefined;
    let close = () => {};
    const schedule = () => {
      clearTimeout(flush);
      flush = window.setTimeout(() => {
        const ids = pending;
        pending = new Set();
        void qc.invalidateQueries({ queryKey: keys.lots });
        for (const id of ids) if (id !== "*") void qc.invalidateQueries({ queryKey: keys.lot(id) });
        if (ids.has("*")) void qc.invalidateQueries({ queryKey: keys.issues });
      }, 250);
    };
    const onEvent = (data: string) => {
      if (data === "pong") return;
      const e = JSON.parse(data);
      if (e.type === "lot_changed") pending.add(e.lotId);
      if (e.type === "issues_changed") pending.add("*");
      if (e.type === "feedback_changed") void qc.invalidateQueries({ queryKey: ["feedback"] });
      if (pending.size) schedule();
    };
    const onOpen = () => {
      retry = 1000;
      connection.set("live");
      void qc.invalidateQueries();
    };
    const onDrop = () => {
      close();
      if (closed) return;
      connection.set(navigator.onLine ? "connecting" : "offline");
      timer = window.setTimeout(connect, (retry = Math.min(retry * 2, 30_000)));
    };
    const connect = () => {
      connection.set("connecting");
      if (transport === "ws") {
        const ws = new WebSocket(`${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/api/live`);
        ws.onopen = () => {
          onOpen();
          ping = window.setInterval(() => ws.readyState === WebSocket.OPEN && ws.send("ping"), 30_000);
        };
        ws.onmessage = (m) => onEvent(String(m.data));
        ws.onclose = () => { clearInterval(ping); onDrop(); };
        close = () => { ws.onclose = null; ws.close(); clearInterval(ping); };
      } else {
        const es = new EventSource("/api/events");
        es.onopen = onOpen;
        es.onmessage = (m) => onEvent(m.data);
        es.onerror = onDrop;
        close = () => es.close();
      }
    };
    connect();
    return () => {
      closed = true;
      close();
      clearTimeout(timer);
      clearTimeout(flush);
    };
  }, [qc, transport]);
}

/**
 * Some sites (HiBid) refuse the server but answer browsers. While Hammer is open and visible,
 * ask the server which of those lots are due and fetch them from here, one at a time.
 */
export function useBrowserAssist() {
  useEffect(() => {
    let stopped = false;
    let running = false;
    const run = async () => {
      if (running || document.visibilityState !== "visible") return;
      running = true;
      try {
        const due = await api<{ lotId: string; request: Parameters<typeof clientFetch>[0] }[]>("/assist/due");
        for (const d of due) {
          if (stopped) break;
          const r = await clientFetch(d.request);
          await api(`/assist/${d.lotId}`, { method: "POST", body: r });
          await new Promise((res) => setTimeout(res, 2000));
        }
      } catch {
        /* offline or logged out; try again next round */
      } finally {
        running = false;
      }
    };
    const t = window.setInterval(run, 60_000);
    const onVis = () => void run();
    document.addEventListener("visibilitychange", onVis);
    void run();
    return () => {
      stopped = true;
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);
}

/** Archive or restore a lot, with Undo. The row leaves (or returns to) the agenda immediately. */
export function useArchiveWithUndo() {
  const qc = useQueryClient();
  const apply = async (lot: LotView, archived: boolean) => {
    const at = archived ? new Date().toISOString() : null;
    qc.setQueryData<LotView[]>(keys.lots, (old) => (archived ? old?.filter((l) => l.id !== lot.id) : old && [...old.filter((l) => l.id !== lot.id), { ...lot, archivedAt: at }]));
    qc.setQueryData<LotView[]>(keys.archived, (old) => (archived ? old && [...old.filter((l) => l.id !== lot.id), { ...lot, archivedAt: at }] : old?.filter((l) => l.id !== lot.id)));
    try {
      await api(`/lots/${lot.id}`, { method: "PATCH", body: { archived } });
    } catch (e) {
      toast(`Couldn't ${archived ? "archive" : "restore"}: ${(e as Error).message}`, { tone: "error" });
    }
    await Promise.all([qc.invalidateQueries({ queryKey: keys.lots }), qc.invalidateQueries({ queryKey: keys.archived }), qc.invalidateQueries({ queryKey: keys.lot(lot.id) }), qc.invalidateQueries({ queryKey: keys.issues })]);
  };
  return (lot: LotView, archived: boolean) => {
    void apply(lot, archived);
    toast(`${archived ? "Archived" : "Restored"}: ${lot.title}`, { action: { label: "Undo", run: () => void apply(lot, !archived) } });
  };
}

// Deletes wait out the Undo window; if the page is closed first, they're sent on the way out.
const pendingDeletes = new Map<string, number>();
const sendDelete = (id: string) => fetch(`/api/lots/${id}`, { method: "DELETE", keepalive: true, credentials: "same-origin" });
window.addEventListener("pagehide", () => {
  for (const [id, t] of pendingDeletes) {
    clearTimeout(t);
    void sendDelete(id);
  }
  pendingDeletes.clear();
});

/** Delete a lot for good, with Undo for a few seconds first. */
export function useDeleteWithUndo() {
  const qc = useQueryClient();
  return (lot: Pick<LotView, "id" | "title">) => {
    const forget = () => {
      pendingDeletes.delete(lot.id);
      deleting.set((d) => { const n = new Set(d); n.delete(lot.id); return n; });
    };
    deleting.set((d) => new Set(d).add(lot.id));
    const t = window.setTimeout(async () => {
      try {
        const r = await sendDelete(lot.id);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
      } catch (e) {
        toast(`Couldn't delete: ${(e as Error).message}`, { tone: "error" });
      }
      forget();
      void qc.invalidateQueries();
    }, 6000);
    pendingDeletes.set(lot.id, t);
    toast(`Deleted: ${lot.title}`, {
      action: {
        label: "Undo",
        run: () => {
          clearTimeout(t);
          forget();
        },
      },
    });
  };
}
